import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Star as StarIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import paleoBackground from '../../images/paleo.png';
import paleoDietFoodImage from '../../images/paleo-diet-food.jpg';
import paleoDietAvifImage from '../../images/Paleo-Diet.avif';
import whatIsPaleoImage from '../../images/what-is-paleo.webp';

const PAGE_URL = 'https://createadish.com/dietary-guide/paleo';
const PAGE_TITLE = 'Paleo Diet Guide: Eat Like Our Ancestors - Create A Dish';
const PAGE_DESCRIPTION = 'Based on foods likely eaten during the Paleolithic era, focusing on whole foods like lean meats, fish, fruits, vegetables, nuts, and seeds while avoiding grains and dairy.';
const PAGE_IMAGE = 'https://createadish.com/images/paleo-og.png';

const Paleo: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="paleo diet, paleolithic diet, caveman diet, whole foods, grains free, ancestral eating, primal diet" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Paleo Diet Guide: Eat Like Our Ancestors",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": { "@type": "Organization", "name": "Create A Dish" },
            "publisher": { "@type": "Organization", "name": "Create A Dish", "logo": { "@type": "ImageObject", "url": "https://createadish.com/logo.png" } },
            "datePublished": "2025-12-24",
            "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL },
            "keywords": "WholeFoods, GrainsFree, Ancestral"
          })}
        </script>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ height: { xs: '40vh', md: '50vh' }, backgroundImage: `url(${paleoBackground})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(1px)', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)', px: 2, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', mb: 2 }}>Paleo Diet</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>Eat Like Our Ancestors</Typography>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              onClick={() => navigate('/dietary-guide')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Dietary Guide
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Paleo Diet
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', color: 'primary.main' }}>The Paleo Diet</Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.1rem' } }}>{PAGE_DESCRIPTION}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            {['#WholeFoods', '#GrainsFree', '#Ancestral'].map(tag => <Typography key={tag} variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{tag}</Typography>)}
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to the Paleo Diet, based on foods likely eaten during the Paleolithic era, focusing on whole foods like lean meats, fish, fruits, vegetables, nuts, and seeds while avoiding grains and dairy. This ancestral approach emphasizes unprocessed, nutrient-dense eating for health and vitality.
          </Typography>
          <Box component="img" src={paleoDietFoodImage} alt="Paleo diet foods" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The Paleo Diet draws from the eating habits of our Paleolithic ancestors, dating back about 2.5 million years to the Stone Age, when humans were hunter-gatherers. Modern interpretations began in the 1970s with gastroenterologist Walter L. Voegtlin's book "The Stone Age Diet," but it gained popularity in the 2000s through Loren Cordain's "The Paleo Diet" (2002).
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Foods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The Paleo Diet prioritizes whole, unprocessed foods that could theoretically be hunted or gathered.
            </Typography>
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Core Foods to Emphasize
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Lean Meats and Proteins</strong>: Grass-fed beef, pork, poultry, wild game.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Fish and Seafood</strong>: Salmon, tuna, shrimp for omega-3s.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Fruits and Vegetables</strong>: Berries, apples, bananas, leafy greens, broccoli, carrots.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Nuts and Seeds</strong>: Almonds, walnuts, sunflower seeds.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Healthy Fats</strong>: Avocados, olive oil, coconut oil.</Typography>
            </Box>
            <Box component="img" src={paleoDietAvifImage} alt="Paleo diet guide" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The Paleo Diet may support weight loss by promoting satiety through protein and fiber, potentially lowering blood pressure and improving gut health. It could reduce risks for heart disease, diabetes, and inflammation.
            </Typography>
            <Box component="img" src={whatIsPaleoImage} alt="What is Paleo" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting the Paleo Diet
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Start Simple</strong>: Focus on swapping processed foods for whole alternatives gradually.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Plan for Nutrients</strong>: Include diverse veggies and consider supplements for calcium or vitamin D.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Use AI</strong>: Generate recipes on CreateADish.com, like "paleo grilled fish with nuts."</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Stay Flexible</strong>: Adapt for modern life while sticking to core principles.</Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>Ready to Create Your Perfect Dish?</Typography>
            <Button variant="contained" size="large" startIcon={<StarIcon />} onClick={() => { setMode('generate'); navigate('/composer'); }} sx={{ minWidth: 200, fontSize: '1.1rem', py: 1.5, px: 4 }}>Get Started</Button>
          </Box>
          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">© 2025 Create A Dish. Made with ❤️ for food lovers everywhere.</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Paleo;
